import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  buildAuthUrlWithNext,
  isAccountUserAuthenticated,
  isAccountUserStaff,
} from "domain/account";
import {
  fetchAuthenticatedUser,
  initializeAuthSession,
  resetAuthSession,
  resolveAuthError,
} from "features/account/lib/auth";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserProfile = useCallback(async () => {
    try {
      setError(null);
      const nextUser = await fetchAuthenticatedUser();
      setUser(nextUser);
      return nextUser;
    } catch (requestError) {
      console.error("Failed to fetch user profile:", requestError);
      setError(resolveAuthError(requestError, "Failed to load user profile"));
      setUser(null);
      resetAuthSession();
      return null;
    }
  }, []);

  const logout = useCallback(() => {
    resetAuthSession();
    setUser(null);
    setError(null);
  }, []);

  useEffect(() => {
    let isActive = true;

    const initializeAuth = async () => {
      try {
        await initializeAuthSession({
          locationSearch: window.location.search,
          loadUser: fetchUserProfile,
          onAuthenticated: ({ nextPath }) => {
            if (!isActive) {
              return;
            }

            if (nextPath) {
              navigate(nextPath || "/", { replace: true });
            }
          },
          onAnonymous: () => {
            if (!isActive) {
              return;
            }

            setUser(null);
          },
        });
      } catch (initError) {
        console.error("Auth initialization error:", initError);
        if (!isActive) {
          return;
        }

        setError(
          resolveAuthError(initError, "Failed to initialize authentication"),
        );
        setUser(null);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isActive = false;
    };
  }, [fetchUserProfile, navigate]);

  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      logout,
      refreshUser: fetchUserProfile,
      isAuthenticated: isAccountUserAuthenticated(user),
      isStaff: isAccountUserStaff(user),
      getAuthUrlWithNext: buildAuthUrlWithNext,
      initialized: !loading,
    }),
    [user, loading, error, logout, fetchUserProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

export default AuthContext;
